import type { CSSProperties, HTMLAttributes, ReactNode } from 'react';
import { ChevronRight, GripVertical, Trash2 } from 'lucide-react';

/**
 * One visual language for every list of items in the app (invoices,
 * deliverables, creators, services, expenses, payments):
 * - RowList: one bordered box with dividers, so rows read as a list;
 * - ExpandableRow: a status stripe, a square arrow at the start that turns
 *   down when open, and the details in a grey panel under the row;
 * - StaticRow: the same stripe for rows edited in place;
 * - ExpandToggle: the same arrow, for tables with expandable rows.
 */

export type RowTone = 'neutral' | 'info' | 'success' | 'warning' | 'danger';

const STRIPE: Record<RowTone, string> = {
  neutral: 'bg-gray-300',
  info: 'bg-blue-500',
  success: 'bg-emerald-500',
  warning: 'bg-amber-400',
  danger: 'bg-red-500',
};

function Stripe({ tone }: { tone: RowTone }) {
  return <span aria-hidden className={`absolute left-0 top-2 bottom-2 w-1 rounded-r-full ${STRIPE[tone]}`} />;
}

export function RowList({ children, className = '' }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={`rounded-xl border border-gray-200 bg-white divide-y divide-gray-200
        [&>*:first-child_[data-row-head]]:rounded-t-xl
        [&>*:last-child_[data-row-head]:not([data-open])]:rounded-b-xl
        [&>*:last-child_[data-row-body]]:rounded-b-xl ${className}`}
    >
      {children}
    </div>
  );
}

/** The square arrow: right when closed, down when open. */
export function ExpandToggle({
  open,
  onClick,
  label,
  className = '',
}: {
  open: boolean;
  /** Without it the arrow is decoration inside something already clickable. */
  onClick?: () => void;
  label?: string;
  className?: string;
}) {
  const classes = `grid place-items-center w-7 h-7 shrink-0 rounded-lg border bg-white transition-colors ${
    open ? 'border-gray-400 text-gray-900' : 'border-gray-200 text-gray-500 hover:border-gray-400 hover:text-gray-900 group-hover:border-gray-400 group-hover:text-gray-900'
  } ${className}`;
  const icon = <ChevronRight className={`w-4 h-4 transition-transform duration-150 ${open ? 'rotate-90' : ''}`} />;
  if (!onClick) return <span aria-hidden className={classes}>{icon}</span>;
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      aria-expanded={open}
      aria-label={label ?? (open ? 'Fechar' : 'Abrir')}
      title={label}
      className={classes}
    >
      {icon}
    </button>
  );
}

interface ExpandableRowProps {
  nodeRef?: (el: HTMLElement | null) => void;
  style?: CSSProperties;
  isDragging?: boolean;
  /** dnd-kit attributes + listeners for the grip; omit to hide it. */
  dragHandle?: HTMLAttributes<HTMLButtonElement> | null;
  tone: RowTone;
  isExpanded: boolean;
  onToggle: () => void;
  /** What the closed row says: name, number, status, dates. */
  summary: ReactNode;
  /** Right-aligned value, usually an amount. */
  aside?: ReactNode;
  /** Extra controls before delete (e.g. a menu). Clicks don't toggle the row. */
  actions?: ReactNode;
  onDelete?: () => void;
  deleteLabel?: string;
  hasError?: boolean;
  ariaLabel?: string;
  children: ReactNode;
}

export function ExpandableRow({
  nodeRef,
  style,
  isDragging,
  dragHandle,
  tone,
  isExpanded,
  onToggle,
  summary,
  aside,
  actions,
  onDelete,
  deleteLabel,
  hasError,
  ariaLabel,
  children,
}: ExpandableRowProps) {
  return (
    <div
      ref={nodeRef}
      style={style}
      className={`relative ${isDragging ? 'z-10 opacity-60 shadow-lg ring-2 ring-accent-400 rounded-xl' : ''} ${hasError ? 'ring-1 ring-inset ring-red-300' : ''}`}
    >
      <Stripe tone={tone} />
      <div
        data-row-head
        data-open={isExpanded || undefined}
        role="button"
        tabIndex={0}
        aria-expanded={isExpanded}
        aria-label={ariaLabel}
        onClick={onToggle}
        onKeyDown={(e) => {
          if (e.target !== e.currentTarget) return;
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onToggle();
          }
        }}
        className={`group flex flex-wrap items-center gap-x-3 gap-y-1 pl-4 pr-2 py-2.5 cursor-pointer select-none transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-ink/30 ${
          isExpanded ? 'bg-gray-50' : 'hover:bg-gray-50'
        }`}
      >
        <ExpandToggle open={isExpanded} />
        {/* On a phone the description gets its own line; the rest goes below */}
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 min-w-0 flex-1 basis-[calc(100%-2.75rem)] sm:basis-0">{summary}</div>
        <span className="ml-auto flex items-center gap-0.5">
          {aside !== undefined && aside !== '' && (
            <span className="text-sm font-semibold text-gray-900 tabular-nums whitespace-nowrap pr-2">{aside}</span>
          )}
          {actions && <span className="flex items-center" onClick={(e) => e.stopPropagation()}>{actions}</span>}
          {onDelete && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onDelete(); }}
              className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
              aria-label={deleteLabel}
              title={deleteLabel}
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
          {dragHandle && (
            <button
              type="button"
              className="p-1.5 rounded-lg text-gray-300 hover:text-gray-600 hover:bg-gray-100 cursor-grab active:cursor-grabbing touch-none"
              aria-label="Arrastar para reordenar"
              {...dragHandle}
              onClick={(e) => e.stopPropagation()}
            >
              <GripVertical className="w-4 h-4" />
            </button>
          )}
        </span>
      </div>
      {isExpanded && (
        <div data-row-body className="bg-gray-50 border-t border-gray-200/70 pl-4 pr-3 pt-3 pb-4 space-y-3">
          {children}
        </div>
      )}
    </div>
  );
}

/** A row edited in place (no details to open), with the status stripe. */
export function StaticRow({ tone, children, className = '', muted = false }: {
  tone: RowTone;
  children: ReactNode;
  className?: string;
  muted?: boolean;
}) {
  return (
    <div className={`relative pl-4 pr-2 py-2 ${muted ? 'opacity-70' : ''} ${className}`}>
      <Stripe tone={tone} />
      {children}
    </div>
  );
}
